import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { SecondaryNavigation } from '@reapitform/dashboard-ui'
import { ITabsItem, Tabs } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { Loader } from '@reapit/elements/deprecated/loader'
import { Text } from '@reapit/elements/utils/text'
import { ElDeprecatedPageHeaderSeparator as PageHeaderSeparator } from '@reapit/elements/deprecated/page-header'
import { useAdminFullForm } from '../../api/full-form'
import { getEnumLabel, getFormFamily, useAdminEnums } from '../../api/enums'
import { ADMIN_FORM_PREVIEW_QUERY_KEY } from '../../api/form-preview'
import { useAccessMode, VersionLockProvider } from '../../access-mode-provider'
import { AgentView } from './view/agent-view'
import { RecipientView } from './view/recipient-view'
import { PdfTemplatesTab } from './view/pdf-templates-tab'
import { PdfMappingTab } from './view/pdf-mapping-tab'
import { NotificationsActionsTab } from './view/notifications-actions-tab'
import { EditDetailsDrawer } from './view/edit-details-drawer'
import { PlaceholderTab } from './view/placeholder-tab'
import {
  BuildGuideText,
  BuilderBody,
  BuilderShell,
  ContentInner,
  HeaderInner,
  LoaderWrap,
  MetaItem,
  MetaRow,
  PageTitle,
  PlaceholderPanel,
} from './style'

// Structural/child field types aren't meaningful link targets on their own (an option belongs to its
// composite; a group/collection is a container) — only real leaf values are offered for linking. Plain
// module constants (not useMemo — they never change, and were never being recreated per-render regardless).
const NON_LINKABLE_EDIT_TYPES = new Set(['Group', 'Collection', 'SelectOption', 'RadioButton'])
// Matches the same literal set used by field-drawer/agent-view — CheckboxGroup isn't authorable yet, so
// it's deliberately excluded here too (keeping it would only add dead code with no way to trigger it).
const COMPOSITE_EDIT_TYPES = new Set(['Select', 'RadioGroup'])
const OPTION_EDIT_TYPES = new Set(['SelectOption', 'RadioButton'])

/**
 * The form builder shell (route `/forms/:formReferenceID/versions/:versionReferenceID`). Mirrors the agent
 * document view: a SecondaryNavigation top bar, a header (form name + metadata line), and five tabs — PDF
 * Templates, Agent App, Recipient App, PDF Mapping, Notifications & Actions — reordered and adapted per the
 * form's document family (see tabItems below; a Notice has no tenant flow, so Recipient App becomes a
 * placeholder and Notifications & Actions moves up to third).
 */
export const BuilderPage: React.FC = () => {
  const navigate = useNavigate()
  const { formReferenceID, versionReferenceID } = useParams()
  const { data, loading, errorString, refresh } = useAdminFullForm(formReferenceID, versionReferenceID)
  const { data: enums, loading: enumsLoading } = useAdminEnums()
  const { isReadOnly } = useAccessMode()
  const [isEditOpen, setIsEditOpen] = useState(false)
  const queryClient = useQueryClient()
  const isLoading = loading || enumsLoading

  // Editing form/version details (e.g. a flag like "allow representee address") changes what the agent/
  // recipient factory previews render, but those previews are a separate query owned by AgentView/RecipientView
  // — refreshing only the full-form graph here leaves them showing stale fields until a hard reload.
  const handleDetailsSaved = () => {
    refresh()
    queryClient.invalidateQueries({ queryKey: ADMIN_FORM_PREVIEW_QUERY_KEY })
  }

  const form = data?.form
  const version = data?.version.version
  const isVersionLocked = version?.isProductionLocked ?? false
  const family = getFormFamily(enums, form?.formType)
  const isNotice = family === 'Notice'

  // The form type is already conveyed by the top bar + heading, so the metadata line carries the rest as
  // bold-label / value pairs (region is dropped when empty, e.g. NZ).
  const metaItems = useMemo(() => {
    if (!form) return []
    const items: { label: string; value: string }[] = []
    const region = getEnumLabel(enums, 'Region', form.region)
    if (region) items.push({ label: 'Region', value: region })
    const country = getEnumLabel(enums, 'Country', form.country)
    if (country) items.push({ label: 'Country', value: country })
    if (version?.version) items.push({ label: 'Version', value: version.version })
    return items
  }, [form, version, enums])

  const formFieldOptions = useMemo(
    () =>
      (data?.version.reviewCards ?? []).flatMap((cardEntry) =>
        cardEntry.sections.flatMap((sectionEntry) => {
          // Options render as a dependency onto their composite parent, so this needs the section's own
          // dependency graph to find a composite's children — same lookup agent-view uses to render them.
          const dependantsOf = (parentReferenceID: string) =>
            sectionEntry.fields.filter((entry) =>
              entry.dependencies.some((dependency) => dependency.dependencyFieldReferenceID === parentReferenceID),
            )

          return sectionEntry.fields
            .filter((fieldEntry) => !NON_LINKABLE_EDIT_TYPES.has(fieldEntry.field.editType))
            .map((fieldEntry) => {
              // For a composite (Select/RadioGroup/CheckboxGroup), its own answer will equal one of these
              // option children's stored value — surfaced so a "Value to Match" picker can offer the exact
              // set of strings the linked field could ever resolve to, instead of free text.
              const answerOptions = COMPOSITE_EDIT_TYPES.has(fieldEntry.field.editType)
                ? dependantsOf(fieldEntry.field.referenceID)
                    .filter((entry) => OPTION_EDIT_TYPES.has(entry.field.editType))
                    .sort((a, b) => a.field.editOrder - b.field.editOrder)
                    .map((entry) => ({
                      value: entry.field.defaultData || entry.field.editDisplayName || '',
                      label: entry.field.editDisplayName || entry.field.defaultData || 'Option',
                    }))
                    .filter((option) => option.value)
                : undefined

              return {
                referenceID: fieldEntry.field.referenceID,
                label: `${cardEntry.card.displayName || 'Untitled card'} · ${
                  sectionEntry.section.displayName || 'Untitled section'
                } · ${fieldEntry.field.editDisplayName || fieldEntry.field.jsonPropertyName || 'Field'}`,
                editType: fieldEntry.field.editType,
                answerOptions,
              }
            })
        }),
      ),
    [data],
  )

  const tabItems: ITabsItem[] = useMemo(() => {
    const pdfTab: ITabsItem = {
      title: 'PDF Templates',
      name: 'pdf',
      component: (
        <PdfTemplatesTab formReferenceID={formReferenceID ?? ''} versionReferenceID={versionReferenceID ?? ''} />
      ),
    }
    const agentTab: ITabsItem = {
      title: 'Agent App',
      name: 'agent',
      component: (
        <AgentView
          reviewCards={data?.version.reviewCards ?? []}
          formReferenceID={formReferenceID ?? ''}
          versionReferenceID={versionReferenceID ?? ''}
          family={family}
          onChanged={() => refresh()}
        />
      ),
    }
    // A Notice has no tenant flow at all — recipients are emailed, never asked to sign — so there is
    // nothing for this tab to configure. Replaced rather than removed/hidden, so the tab list stays a
    // stable set of positions and the placeholder actively teaches the model instead of leaving a gap.
    const recipientTab: ITabsItem = {
      title: 'Recipient App',
      name: 'recipient',
      component: isNotice ? (
        <PlaceholderTab message="Notices have no recipient app — tenants are emailed rather than asked to sign. Configure the email tenants receive under Notifications & Actions." />
      ) : (
        <RecipientView
          recipientSteps={data?.version.recipientSteps ?? []}
          reviewCards={(data?.version.reviewCards ?? []).map((entry) => entry.card)}
          formFieldOptions={formFieldOptions}
          formReferenceID={formReferenceID ?? ''}
          versionReferenceID={versionReferenceID ?? ''}
          onChanged={() => refresh()}
        />
      ),
    }
    const pdfMappingTab: ITabsItem = {
      title: 'PDF Mapping',
      name: 'pdf-mapping',
      component: (
        <PdfMappingTab
          formReferenceID={formReferenceID ?? ''}
          versionReferenceID={versionReferenceID ?? ''}
          formFieldOptions={formFieldOptions}
        />
      ),
    }
    const notificationsTab: ITabsItem = {
      title: 'Notifications & Actions',
      name: 'notifications-actions',
      component: (
        <NotificationsActionsTab
          formReferenceID={formReferenceID ?? ''}
          versionReferenceID={versionReferenceID ?? ''}
          eventTemplates={data?.eventTemplates ?? []}
          actionTemplates={data?.actionTemplates ?? []}
          family={family}
          onChanged={() => refresh()}
        />
      ),
    }

    // A Notice's whole lifecycle hinges on what gets emailed at send time, so Notifications & Actions
    // moves up to third — load-bearing rather than an afterthought at the end of the tab strip.
    return isNotice
      ? [pdfTab, agentTab, notificationsTab, recipientTab, pdfMappingTab]
      : [pdfTab, agentTab, recipientTab, pdfMappingTab, notificationsTab]
  }, [data, formFieldOptions, formReferenceID, versionReferenceID, refresh, family, isNotice])

  const title = form?.fullName || form?.name || 'Form Builder'

  return (
    <BuilderShell>
      <SecondaryNavigation title={title} exitBtnText="Exit" onExit={() => navigate('/forms')} />
      <BuilderBody>
        {isLoading && (
          <LoaderWrap>
            <Loader label="Loading form…" />
          </LoaderWrap>
        )}
        {!isLoading && (errorString || !data) && (
          <ContentInner>
            <PlaceholderPanel>Couldn’t load this form.{errorString ? ` (${errorString})` : ''}</PlaceholderPanel>
          </ContentInner>
        )}
        {!isLoading && data && form && (
          <>
            <HeaderInner>
              <PageTitle>{form.name || 'Untitled form'}</PageTitle>
              <MetaRow>
                {metaItems.map((item, index) => (
                  <React.Fragment key={item.label}>
                    <MetaItem>
                      <Text font="text-base/bold" colour="primary">
                        {item.label}:
                      </Text>
                      <Text font="text-base/regular" colour="primary">
                        {item.value}
                      </Text>
                    </MetaItem>
                    {index < metaItems.length - 1 && <PageHeaderSeparator />}
                  </React.Fragment>
                ))}
                {!isReadOnly && !isVersionLocked && (
                  <Button
                    variant="tertiary"
                    useLinkStyle
                    size="small"
                    type="button"
                    onClick={() => setIsEditOpen(true)}
                  >
                    Edit Details
                  </Button>
                )}
              </MetaRow>
            </HeaderInner>
            <VersionLockProvider isLocked={isVersionLocked}>
              <ContentInner>
                <BuildGuideText>
                  {isNotice ? (
                    <>
                      Work through the tabs left to right. Start with PDF Templates to upload the PDF this form fills
                      in, then Agent App to build the fields the agent completes — including a Signature section for the
                      property manager, since a Notice is signed by them alone at send time. Notifications & Actions is
                      where sending a Notice actually happens: it&apos;s what emails the PDF to tenants, so a Notice
                      with nothing configured there will send with no notification at all. Notices have no recipient app
                      — tenants are emailed, not asked to sign — so PDF Mapping is the last step, deciding what gets
                      written onto the PDF.
                    </>
                  ) : (
                    <>
                      Work through the tabs left to right. Start with PDF Templates to upload the PDF this form fills
                      in, then Agent App to build the review fields the agent completes. From there, Recipient App and
                      PDF Mapping can link back to those fields — Recipient App to control what the recipient sees, and
                      PDF Mapping to decide what gets written onto the PDF.
                    </>
                  )}
                </BuildGuideText>
                <Tabs tabItems={tabItems} />
              </ContentInner>
            </VersionLockProvider>
            <EditDetailsDrawer
              isOpen={isEditOpen}
              onOpenChange={setIsEditOpen}
              formReferenceID={formReferenceID ?? ''}
              versionReferenceID={versionReferenceID ?? ''}
              form={form}
              version={data.version.version}
              onSaved={handleDetailsSaved}
            />
          </>
        )}
      </BuilderBody>
    </BuilderShell>
  )
}

export default BuilderPage
